import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Draggable from 'react-draggable';
import { CheckCircle, KeyboardDoubleArrowDown, Close, CloudUpload, AutoFixHigh, ErrorOutline, InfoOutlined, DragIndicator } from '@mui/icons-material';
import socket, { showMessage } from '../../config';
import { toggleModal } from '../../reducers/modalReducer';
import { updateFileAllocation } from '../../reducers/filesAllocationReducer';

const STORAGE_KEY = 'autoFillStatusBar';
const getStoredState = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; } catch { return null; } };
const saveState = (state) => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {} };
const clearStoredState = () => { try { localStorage.removeItem(STORAGE_KEY); } catch {} };

const barBg = { background: '#E5E7EB', borderRadius: '10px', height: '8px', overflow: 'hidden', marginBottom: '0.75rem' };
const barFill = (w) => ({ background: 'linear-gradient(90deg, #4a67ff 0%, #3d56d9 100%)', height: '100%', borderRadius: '10px', width: `${w}%`, transition: 'width 0.3s ease' });
const statBox = (bg) => ({ background: bg, borderRadius: '8px', padding: '0.5rem', textAlign: 'center' });
const ellipsis = { margin: 0, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const ProgressBar = ({ percent }) => (<div style={barBg}><div style={barFill(percent)} /></div>);

const AutoFillStatusBar = () => {
    const [isExpanded, setIsExpanded] = useState(true);
    const nodeRef = useRef(null);
    const dispatch = useDispatch();
    const { fileReport, autoFillContext } = useSelector(state => state.fileAllocation);
    const lastSocketEvent = useRef(0);
    const wasRestoredFromStorage = useRef(false);

    // Use local state for all status bar data to avoid triggering re-renders in Company/index.js
    const [status, setStatus] = useState({ visible: false, phase: '', moduleName: '', total: 0, current: 0, currentQuestion: '', uploadProgress: null, processed: 0, answered: 0, needsReview: 0, skipped: 0, error: '', indexingProgress: null });

    // Sync from Redux modal → local state (only for initial triggers from AutoFillButton/QuesAns)
    const modal = useSelector(state => state.modal);
    const prevPhaseRef = useRef('');
    useEffect(() => {
        if (modal.autoFillStatusBarVisible && !status.visible) {
            setStatus(s => ({ ...s, visible: true, phase: modal.autoFillStatusBarPhase || s.phase, moduleName: modal.autoFillModuleName || s.moduleName, uploadProgress: modal.autoFillUploadProgress || s.uploadProgress }));
        }
        // Sync phase changes initiated externally (e.g., uploading→indexing→allocated from AutoFillButton)
        if (modal.autoFillStatusBarPhase && modal.autoFillStatusBarPhase !== prevPhaseRef.current && modal.autoFillStatusBarPhase !== status.phase) {
            prevPhaseRef.current = modal.autoFillStatusBarPhase;
            setStatus(s => ({ ...s, phase: modal.autoFillStatusBarPhase, moduleName: modal.autoFillModuleName || s.moduleName, uploadProgress: modal.autoFillUploadProgress ?? s.uploadProgress, indexingProgress: modal.autoFillIndexingProgress ?? s.indexingProgress }));
        }
    }, [modal.autoFillStatusBarVisible, modal.autoFillStatusBarPhase, modal.autoFillModuleName, modal.autoFillUploadProgress, modal.autoFillIndexingProgress]);

    // Restore state from localStorage on mount (handles page refresh)
    useEffect(() => {
        const stored = getStoredState();
        if (!stored || !stored.autoFillStatusBarVisible || status.visible) return;
        const activePhases = ['uploading', 'indexing', 'filling'];
        if (activePhases.includes(stored.autoFillStatusBarPhase)) {
            wasRestoredFromStorage.current = true;
            if (stored.autoFillContext) dispatch(updateFileAllocation({ autoFillContext: stored.autoFillContext, tab: stored.autoFillContext.moduleType }));
            setStatus({ visible: true, phase: stored.autoFillStatusBarPhase, moduleName: stored.autoFillModuleName || '', total: stored.autoFillTotal || 0, current: stored.autoFillCurrent || 0, currentQuestion: stored.autoFillCurrentQuestion || '', uploadProgress: stored.autoFillUploadProgress || null, processed: 0, answered: 0, needsReview: 0, skipped: 0, error: '', indexingProgress: stored.autoFillIndexingProgress || null });
            const restoreTime = Date.now();
            const staleTimer = setTimeout(() => {
                if (lastSocketEvent.current < restoreTime) setStatus(s => ({ ...s, phase: 'stale' }));
            }, 15000);
            return () => clearTimeout(staleTimer);
        } else if (stored.autoFillStatusBarPhase === 'allocated') {
            if (stored.autoFillContext) dispatch(updateFileAllocation({ autoFillContext: stored.autoFillContext, tab: stored.autoFillContext.moduleType }));
            setStatus({ visible: true, phase: 'allocated', moduleName: stored.autoFillModuleName || '', total: 0, current: 0, currentQuestion: '', uploadProgress: null, processed: 0, answered: 0, needsReview: 0, skipped: 0, error: '', indexingProgress: null });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Persist state to localStorage whenever it changes
    useEffect(() => {
        if (status.visible) {
            const ctx = autoFillContext ? { enabled: autoFillContext.enabled, companyId: autoFillContext.companyId, moduleType: autoFillContext.moduleType, sectionCode: autoFillContext.sectionCode, scope: autoFillContext.scope, scopeName: autoFillContext.scopeName } : null;
            saveState({ autoFillStatusBarVisible: true, autoFillStatusBarPhase: status.phase, autoFillModuleName: status.moduleName, autoFillTotal: status.total, autoFillCurrent: status.current, autoFillCurrentQuestion: status.currentQuestion, autoFillUploadProgress: status.uploadProgress, autoFillProcessed: status.processed, autoFillAnswered: status.answered, autoFillNeedsReview: status.needsReview, autoFillSkipped: status.skipped, autoFillError: status.error, autoFillIndexingProgress: status.indexingProgress, autoFillContext: ctx });
        } else clearStoredState();
    }, [status, autoFillContext]);

    // Socket events — update local state only, no Redux dispatches
    useEffect(() => {
        const handleProgress = (data) => {
            lastSocketEvent.current = Date.now();
            setStatus(s => {
                if (s.phase === 'allocated') return { ...s, phase: 'filling', total: data.total || 0, current: data.current || 0, currentQuestion: data.currentQuestion || '' };
                if (s.phase !== 'filling') return s;
                return { ...s, total: data.total || 0, current: data.current || 0, currentQuestion: data.currentQuestion || '' };
            });
        };

        const handleCompleted = (data) => {
            lastSocketEvent.current = Date.now();
            setStatus(s => ({ ...s, phase: 'completed', processed: data.processed || 0, answered: data.answered || 0, needsReview: data.needsReview || 0, skipped: data.skipped || 0 }));
        };

        const handleIndexingProgress = (data) => {
            lastSocketEvent.current = Date.now();
            setStatus(s => {
                if (s.phase !== 'indexing') return s;
                return { ...s, indexingProgress: { total: data.total || 0, current: data.current || 0, currentFile: data.currentFile || '' } };
            });
        };

        const handleAllocationCompleted = (data) => {
            lastSocketEvent.current = Date.now();
            if (wasRestoredFromStorage.current) {
                setStatus(s => ({ ...s, phase: 'allocated' }));
                if (data?.fileReport) dispatch(updateFileAllocation({ fileReport: data.fileReport }));
            }
        };

        socket.on('autoFill:progress', handleProgress);
        socket.on('autoFill:completed', handleCompleted);
        socket.on('indexing:progress', handleIndexingProgress);
        socket.on('allocation:completed', handleAllocationCompleted);
        return () => {
            socket.off('autoFill:progress', handleProgress);
            socket.off('autoFill:completed', handleCompleted);
            socket.off('indexing:progress', handleIndexingProgress);
            socket.off('allocation:completed', handleAllocationCompleted);
        };
    }, [dispatch]);

    if (!status.visible) return null;

    const handleClose = () => {
        const wasCompleted = status.phase === 'completed';
        setStatus({ visible: false, phase: '', moduleName: '', total: 0, current: 0, currentQuestion: '', uploadProgress: null, processed: 0, answered: 0, needsReview: 0, skipped: 0, error: '', indexingProgress: null });
        dispatch(toggleModal({ autoFillStatusBarVisible: false, autoFillStatusBarPhase: '' }));
        if (wasCompleted) window.location.reload();
    };

    const getHeaderText = () => {
        switch (status.phase) {
            case 'uploading': { const p = status.uploadProgress || {}; return `Uploading ${p.current || 0} of ${p.total || 0} files`; }
            case 'indexing': { const idx = status.indexingProgress || {}; return idx.total > 0 ? `Indexing ${idx.current || 0} of ${idx.total} files` : 'Indexing files...'; }
            case 'allocated': return 'Files allocated - tap to review';
            case 'filling': return status.total > 0 ? `Auto-filling ${status.current || 0} of ${status.total} questions` : 'Analyzing questions...';
            case 'completed': return 'Auto-Fill Complete!';
            case 'error': return 'Auto-Fill Failed';
            case 'stale': return 'Process may have completed';
            default: return 'Auto-Fill';
        }
    };

    const getPhaseIcon = () => {
        switch (status.phase) {
            case 'uploading': return <CloudUpload style={{ fontSize: '20px' }} />;
            case 'indexing': case 'filling': return <AutoFixHigh style={{ fontSize: '20px' }} className="auto-fill-status-spin" />;
            case 'allocated': return <CheckCircle style={{ fontSize: '20px', color: '#4a67ff' }} />;
            case 'completed': return <CheckCircle style={{ fontSize: '20px', color: '#059669' }} />;
            case 'error': return <ErrorOutline style={{ fontSize: '20px', color: '#DC2626' }} />;
            case 'stale': return <InfoOutlined style={{ fontSize: '20px', color: '#D97706' }} />;
            default: return null;
        }
    };

    const getProgressPercent = () => {
        if (status.phase === 'uploading') { const p = status.uploadProgress || {}; return p.total > 0 ? Math.round((p.current / p.total) * 100) : 0; }
        if (status.phase === 'filling') return status.total > 0 ? Math.round((status.current / status.total) * 100) : 0;
        if (status.phase === 'completed') return 100;
        return 0;
    };

    const canClose = ['completed', 'error', 'stale'].includes(status.phase);

    const handleExpand = () => {
        if (status.phase !== 'allocated') return;
        if (!fileReport?.importResult) { showMessage('Allocation data is still loading, please wait...'); return; }
        setStatus(s => ({ ...s, visible: false }));
        dispatch(toggleModal({ open: 'importSummary', from: '', autoFillStatusBarVisible: false }));
    };

    return (
        <Draggable nodeRef={nodeRef} handle=".auto-fill-drag-handle" bounds="parent">
        <div ref={nodeRef} className='pf bg1 shadow' style={{ zIndex: '10000', right: '10px', bottom: '80px', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', width: '380px' }}>
            {/* Header */}
            <div className="df aic jcsb w100 pl1rem auto-fill-drag-handle" style={{ height: '60px', borderBottom: isExpanded ? '1px solid #eaeaea' : 'none', cursor: 'grab' }}>
                <div className="df aic" style={{ gap: '8px', flex: 1, overflow: 'hidden' }}>
                    <DragIndicator style={{ fontSize: '18px', color: '#9CA3AF', flexShrink: 0 }} />
                    {getPhaseIcon()}
                    <h3 className="c15 fs14px" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getHeaderText()}</h3>
                </div>
                <div className="df aic jcfe">
                    <div className="cp px1rem c15 df aic" onClick={() => setIsExpanded(!isExpanded)} style={{ transition: 'transform 0.3s ease', transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)' }}>
                        <KeyboardDoubleArrowDown />
                    </div>
                    {canClose && <div className="cp px1rem c15 df aic" onClick={handleClose}><Close style={{ fontSize: '20px' }} /></div>}
                </div>
            </div>

            {/* Content */}
            <div className="w100" style={{ maxHeight: isExpanded ? '300px' : '0px', overflowY: 'auto', transition: 'max-height 0.3s ease, opacity 0.3s ease', opacity: isExpanded ? 1 : 0 }}>
                <div style={{ padding: '1rem' }}>
                    {status.moduleName && <p className="c4 fw600 fs12px" style={{ margin: '0 0 0.75rem', textAlign: 'center' }}>{status.moduleName}</p>}

                    {/* Uploading */}
                    {status.phase === 'uploading' && (<>
                        <ProgressBar percent={getProgressPercent()} />
                        {status.uploadProgress?.fileName && <p className="c27 fs12px" style={ellipsis}>{status.uploadProgress.fileName}</p>}
                    </>)}

                    {/* Indexing */}
                    {status.phase === 'indexing' && (() => {
                        const idx = status.indexingProgress || {};
                        const pct = idx.total > 0 ? Math.round((idx.current / idx.total) * 100) : 0;
                        return idx.total > 0 ? (<div>
                            <ProgressBar percent={pct} />
                            <p className="c15 fw500 fs12px" style={{ margin: '0 0 0.25rem', textAlign: 'center' }}>{pct}% complete</p>
                            {idx.currentFile && <p className="c27 fs12px" style={ellipsis}>{idx.currentFile}</p>}
                        </div>) : (<div style={{ textAlign: 'center' }}>
                            <progress color="green" style={{ border: 'none', width: '90%' }} />
                            <p className="c27 fs12px" style={{ margin: '0.5rem 0 0' }}>Processing and indexing your files...</p>
                        </div>);
                    })()}

                    {/* Allocated */}
                    {status.phase === 'allocated' && (
                        <div style={{ textAlign: 'center' }}>
                            <p className="c15 fw500 fs12px" style={{ margin: '0 0 0.75rem' }}>Files have been allocated to sections.</p>
                            <button onClick={handleExpand} className="bg3 bg4-hover c1 br10px px2rem py0-5rem cp fw500 fs12px">Review Allocation</button>
                        </div>
                    )}

                    {/* Filling */}
                    {status.phase === 'filling' && (<>
                        <ProgressBar percent={getProgressPercent()} />
                        <p className="c15 fw500 fs12px" style={{ margin: '0 0 0.25rem', textAlign: 'center' }}>{status.total > 0 ? `${getProgressPercent()}% complete` : 'Analyzing questions...'}</p>
                        {status.currentQuestion && <p className="c27 fs12px" style={ellipsis}>Current: {status.currentQuestion}</p>}
                    </>)}

                    {/* Completed */}
                    {status.phase === 'completed' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                            <div style={statBox('#F3F4F6')}><div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1F2937' }}>{status.processed || 0}</div><div style={{ fontSize: '0.65rem', color: '#6B7280' }}>Processed</div></div>
                            <div style={statBox('#D1FAE5')}><div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#059669' }}>{status.answered || 0}</div><div style={{ fontSize: '0.65rem', color: '#059669' }}>Answered</div></div>
                            <div style={statBox('#FEF3C7')}><div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#D97706' }}>{status.needsReview || 0}</div><div style={{ fontSize: '0.65rem', color: '#D97706' }}>Needs Review</div></div>
                            <div style={statBox('#F3F4F6')}><div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#6B7280' }}>{status.skipped || 0}</div><div style={{ fontSize: '0.65rem', color: '#6B7280' }}>Skipped</div></div>
                        </div>
                    )}

                    {/* Error */}
                    {status.phase === 'error' && <p style={{ color: '#DC2626', fontSize: '0.85rem', textAlign: 'center', margin: 0 }}>{status.error || 'Something went wrong. Please try again.'}</p>}

                    {/* Stale */}
                    {status.phase === 'stale' && <p className="c27 fs12px" style={{ textAlign: 'center', margin: 0 }}>The page was refreshed. Please check your module for updates.</p>}
                </div>
            </div>
        </div>
        </Draggable>
    );
};

export default AutoFillStatusBar;
